import type { CometChatToken } from '@/store/api/cometChatApi';
import type { CometChatSettings, CometChatWidget } from '@/types/cometChat';

import { requestCometChatToken } from '@/store/api/cometChatApi';

interface Viewer {
  uid: string;
  onReady: () => void;
  onReset: () => void;
  onClose: () => void;
}

interface Lease extends Viewer {
  controller: AbortController;
  ready: boolean;
}

interface Dependencies {
  config: { appID: string; appRegion: string; widgetID: string };
  getWidget: () => CometChatWidget | undefined;
  requestToken: (signal: AbortSignal) => Promise<CometChatToken>;
  loadSettings: (signal: AbortSignal) => Promise<CometChatSettings | undefined>;
}

const widgetRoots = () => [
  ...document.querySelectorAll<HTMLElement>('#cometchat__widget'),
];
const setWidgetHidden = (node: HTMLElement, hidden: boolean) => {
  if (node.style.display !== (hidden ? 'none' : 'block')) {
    // eslint-disable-next-line no-param-reassign -- Vendor-owned launcher node.
    node.style.display = hidden ? 'none' : 'block';
  }
};

// The SDK is a global singleton. Serialize its non-abortable writes across
// component mounts; HTTP requests can be aborted/ignored without blocking it.
export const createCometChatSession = (dependencies: Dependencies) => {
  let current: Lease | undefined;
  let suspended = false;
  let sdkOwner: Lease | undefined;
  let sdkWidget: CometChatWidget | undefined;
  let queue = Promise.resolve();
  let hidden = true;
  let observer: MutationObserver | undefined;
  const listening = new WeakSet<CometChatWidget>();
  const rootOwners = new WeakMap<HTMLElement, Lease>();
  const live = (lease: Lease) =>
    !suspended && current === lease && !lease.controller.signal.aborted;
  const ready = (lease: Lease) =>
    live(lease) && lease.ready && sdkOwner === lease;

  const applyVisibility = (reveal = false) => {
    widgetRoots().forEach((node) => {
      const shouldHide =
        hidden ||
        !current ||
        !ready(current) ||
        rootOwners.get(node) !== current;
      // Do not fight other features that temporarily hide the active widget.
      if (shouldHide || reveal) setWidgetHidden(node, shouldHide);
    });
  };
  const close = () => {
    hidden = true;
    applyVisibility();
    try {
      sdkWidget?.openOrCloseChat(false);
    } catch {
      // An uninitialized/unavailable vendor widget must not prevent cleanup.
    }
  };
  const disconnectWhenUnused = () => {
    // Other features restore saved display styles during their own cleanup.
    // Keep guarding retained vendor roots after logout until those roots leave.
    if (!current && !sdkOwner && widgetRoots().length === 0) {
      observer?.disconnect();
      observer = undefined;
    }
  };
  const enqueue = (operation: () => Promise<void>) => {
    queue = queue.then(operation).catch(close).finally(disconnectWhenUnused);
  };
  const logout = async (widget: CometChatWidget) => {
    if (await widget.CometChat.getLoggedinUser()) await widget.logout();
  };
  const releaseSdk = async (lease: Lease) => {
    if (sdkOwner !== lease || !sdkWidget) return;
    close();
    await logout(sdkWidget);
    if (sdkOwner === lease) sdkOwner = undefined;
  };
  const invalidate = (lease: Lease) => {
    lease.controller.abort();
    // eslint-disable-next-line no-param-reassign -- Private, mutable ownership lease.
    lease.ready = false;
    if (current === lease) current = undefined;
    close();
    lease.onReset();
    enqueue(() => releaseSdk(lease));
  };
  const clear = () => {
    if (current) invalidate(current);
    else {
      close();
      const owner = sdkOwner;
      if (owner) enqueue(() => releaseSdk(owner));
    }
  };
  const suspend = () => {
    suspended = true;
    clear();
  };
  const resume = () => {
    clear();
    suspended = false;
  };
  const listen = (widget: CometChatWidget) => {
    if (listening.has(widget)) return;
    listening.add(widget);
    widget.on('openChat', () => {
      if (!current || !ready(current)) close();
    });
    widget.on('closeChat', () => {
      if (current && ready(current)) current.onClose();
    });
  };
  const authenticate = async (
    lease: Lease,
    token: CometChatToken,
    settings?: CometChatSettings,
  ) => {
    if (!live(lease)) return;
    const widget = dependencies.getWidget();
    if (!widget) {
      invalidate(lease);
      return;
    }
    sdkWidget = widget;
    sdkOwner = lease;
    try {
      await widget.init({
        appID: dependencies.config.appID,
        appRegion: dependencies.config.appRegion,
      });
      if (live(lease)) await logout(widget); // Also discard a vendor-persisted previous viewer.
      if (live(lease)) {
        const user = await widget.login({ authToken: token.authToken });
        if (user.getUid() !== lease.uid)
          throw new Error('Chat identity mismatch');
      }
      if (live(lease)) {
        listen(widget);
        const previousRoots = new Set(widgetRoots());
        await widget.launch({
          defaultType: 'user',
          docked: 'true',
          height: '520px',
          roundedCorners: 'true',
          ...(settings ? { settings } : {}),
          target: '#cometchat',
          widgetID: dependencies.config.widgetID,
          width: '400px',
        });
        widgetRoots().forEach((node) => {
          if (!previousRoots.has(node)) rootOwners.set(node, lease);
        });
      }
      if (!live(lease)) {
        await releaseSdk(lease);
        return;
      }
      // eslint-disable-next-line no-param-reassign -- Private, mutable ownership lease.
      lease.ready = true;
      lease.onReady();
    } catch {
      if (live(lease)) invalidate(lease);
      // Cleanup is queued after this SDK operation, never concurrent with login.
    }
  };
  const start = (viewer: Viewer) => {
    clear();
    // Cache reset/refetch can remount Layout before platform logout finishes.
    // Only a successful new platform login may reopen admission in this SPA.
    if (suspended) {
      viewer.onReset();
      return () => {};
    }
    const lease: Lease = {
      ...viewer,
      controller: new AbortController(),
      ready: false,
    };
    current = lease;
    close();
    if (!observer) {
      observer = new MutationObserver(() => {
        applyVisibility();
        disconnectWhenUnused();
      });
      observer.observe(document.body, {
        attributeFilter: ['style'],
        attributes: true,
        childList: true,
        subtree: true,
      });
    }
    const prepare = async () => {
      if (!live(lease)) return;
      if (
        !lease.uid ||
        !dependencies.getWidget() ||
        Object.values(dependencies.config).some((value) => !value)
      ) {
        invalidate(lease);
        return;
      }
      try {
        const token = await dependencies.requestToken(lease.controller.signal);
        if (!live(lease)) return;
        if (token.uid !== lease.uid) throw new Error('Chat identity mismatch');
        const settings = await dependencies
          .loadSettings(lease.controller.signal)
          .catch(() => undefined);
        if (live(lease)) enqueue(() => authenticate(lease, token, settings));
      } catch {
        if (live(lease)) invalidate(lease);
      }
    };
    // StrictMode's immediate setup/cleanup/setup should issue only one request.
    Promise.resolve().then(prepare).catch(close);
    return () => {
      if (current === lease) invalidate(lease);
    };
  };
  const setHidden = (uid: string, requestedHidden: boolean) => {
    hidden =
      requestedHidden || !current || current.uid !== uid || !ready(current);
    applyVisibility(true);
  };
  const openChat = (uid: string, recipient: string) => {
    if (!current || current.uid !== uid || !ready(current) || !recipient)
      return;
    try {
      sdkWidget?.chatWithUser(recipient);
      sdkWidget?.openOrCloseChat(true);
    } catch {
      clear();
    }
  };
  return { clear, openChat, resume, setHidden, start, suspend };
};

const config = {
  appID: import.meta.env.VITE_PUBLIC_COMETCHAT_APP_ID,
  appRegion: import.meta.env.VITE_PUBLIC_COMETCHAT_REGION,
  widgetID: import.meta.env.VITE_PUBLIC_COMETCHAT_WIDGET_ID,
};

const loadSettings = async (
  signal: AbortSignal,
): Promise<CometChatSettings> => {
  const response = await fetch(
    `https://widget-${config.appRegion}.cometchat.io/v2/widget?id=${config.widgetID}`,
    { signal },
  );
  if (!response.ok) throw new Error('Chat settings unavailable');
  const payload = (await response.json()) as {
    data?: { configuration?: CometChatSettings };
  };
  const settings = payload.data?.configuration;
  if (!settings || typeof settings !== 'object')
    throw new Error('Chat settings unavailable');
  return {
    ...settings,
    style: {
      ...settings.style,
      custom_css: [
        settings.style?.custom_css?.trim(),
        '.chat__header .chat__sidebar-menu {\n  background-color: #637A6F !important;\n}',
      ]
        .filter(Boolean)
        .join('\n\n'),
    },
  };
};

export const cometChatSession = createCometChatSession({
  config,
  getWidget: () => window.CometChatWidget,
  loadSettings,
  requestToken: requestCometChatToken,
});
